import { createContext, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import type { ImageVariations } from '#src/lib/share.ts';

export interface Options {
  description?: string;
  img?: string | Record<ImageVariations, string | null> | null;
  twitter?: string;
  replaceFullTitle?: boolean;
  noindex?: boolean;
}

/**
 * Declared by a route's `meta.ts` and applied by `RouteMeta`. It sits between
 * the `SeoProvider` defaults and whatever a page sets through
 * `setPageMetadata`, and each property is optional so an unresolved one falls
 * back instead of rendering blank.
 */
export interface RouteMetadata {
  title?: string;
  description?: string;
  img?: string;
}

type SeoContext = {
  setPageMetadata: (seoTitle: string, options?: Options) => () => void;
  /** Drops canonical and hreflang until the returned cleanup runs. */
  registerNoIndex: () => () => void;
  /**
   * Restricts the hreflang alternates to these language codes until the
   * returned cleanup runs. Nested restrictions intersect; none drops them.
   */
  registerAlternateLanguages: (languages: readonly string[]) => () => void;
  setRouteMetadata: (metadata: RouteMetadata) => () => void;
};

export const SeoContext = createContext<SeoContext | null>(null);

export const useSeoContext = () => useContext(SeoContext)!;

export const NoIndex = () => {
  const registerNoIndex = useContext(SeoContext)?.registerNoIndex;

  useEffect(() => registerNoIndex?.(), [registerNoIndex]);

  return (
    <Helmet>
      <meta name="robots" content="noindex" />
    </Helmet>
  );
};

/** Pass a stable array: a new one re-registers on every render. */
export const AlternateLanguages = ({
  languages,
}: {
  languages: readonly string[];
}) => {
  const registerAlternateLanguages =
    useContext(SeoContext)?.registerAlternateLanguages;

  useEffect(
    () => registerAlternateLanguages?.(languages),
    [registerAlternateLanguages, languages]
  );

  return null;
};
